"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.hasStrategyPreReqs = exports.lookupUser = exports.lookupProximity = exports.lookupValue = exports.LookupConfigSchema = void 0;
exports.LookupConfigSchema = {
    description: 'Lookup Strategy',
    type: 'string',
    enum: ['Offline Only', 'Online Only', 'Online with Offline Fallback', 'Offline with Online Fallback'],
    default: 'Offline with Online Fallback',
};
const successfulResults = (results) => {
    return { results, lookupSucceeded: true };
};
const failedResults = () => {
    return { results: [], lookupSucceeded: false };
};
const getStrategyForAccessors = (strategy, onlineLookup, offlineLookup) => {
    if (onlineLookup && !offlineLookup) {
        return 'Online Only';
    }
    if (offlineLookup && !onlineLookup) {
        return 'Offline Only';
    }
    return strategy;
};
const buildLookupPromise = (lookup, lookupParams) => {
    return () => Promise.resolve(lookup
        ? lookup(lookupParams.aggregateType, lookupParams.attributeName, lookupParams.attributeValue, lookupParams.limit)
        : [])
        .then(successfulResults)
        .catch(failedResults);
};
const buildProximityPromise = (lookup, lookupParams) => {
    return () => Promise.resolve(lookup
        ? lookup(lookupParams.aggregateTypes, lookupParams.centerLatitude, lookupParams.centerLongitude, lookupParams.proximityDistance, lookupParams.distanceUnit, lookupParams.limit)
        : [])
        .then(r => ({ results: r, lookupSucceeded: true }))
        .catch(() => ({ results: [], lookupSucceeded: false }));
};
const buildUserPromise = (lookup, lookupParams) => {
    return () => Promise.resolve(lookup ? lookup(lookupParams.email, lookupParams.username) : null)
        .then(r => ({ result: r, lookupSucceeded: true }))
        .catch(() => ({ result: null, lookupSucceeded: false }));
};
const lookupValue = async (onlineLookup, offlineLookup, strategy, lookupParams) => {
    // These functions are just helpers to return an appropriate promisified version of the lookup
    const onlineLookupPromise = buildLookupPromise(onlineLookup, lookupParams);
    const offlineLookupPromise = buildLookupPromise(offlineLookup, lookupParams);
    strategy = getStrategyForAccessors(strategy, onlineLookup, offlineLookup);
    // use the strategy to decide how to perform the lookup
    switch (strategy) {
        case 'Online Only':
            return onlineLookupPromise();
        case 'Offline Only':
            return offlineLookupPromise();
        case 'Both':
            return Promise.all([onlineLookupPromise(), offlineLookupPromise()])
                .then(promiseResults => {
                // When checking both, we should combine the results and use the lookupSucceeded value from the online lookup
                const results = [];
                if (promiseResults[0].lookupSucceeded) {
                    results.push(...promiseResults[0].results);
                }
                if (promiseResults[1].lookupSucceeded) {
                    for (const foundOfflineItem of promiseResults[1].results) {
                        const foundOnlineItemIndex = results.findIndex(existingResult => existingResult.id === foundOfflineItem.id);
                        // If we find the item both online and off, use the offline one
                        //TODO: Change this to use the lastUpdatedTime to resolve conflicts
                        if (foundOnlineItemIndex < 0) {
                            //We don't have it already, so add it
                            results.push(foundOfflineItem);
                        }
                        else {
                            //replace the online version with our offline one, in case it hasn't been synced yet.
                            results[foundOnlineItemIndex] = foundOfflineItem;
                        }
                    }
                }
                return {
                    results: results.slice(0, lookupParams.limit),
                    lookupSucceeded: promiseResults[0].lookupSucceeded && promiseResults[1].lookupSucceeded,
                };
            })
                .catch(failedResults);
        case 'Offline with Online Fallback':
            const offlineResults = await offlineLookupPromise();
            if (offlineResults && offlineResults.results.length > 0) {
                return offlineResults;
            }
            else {
                return onlineLookupPromise();
            }
        case 'Online with Offline Fallback':
            const onlineResults = await onlineLookupPromise();
            if (onlineResults && onlineResults.results.length > 0) {
                return onlineResults;
            }
            else {
                return offlineLookupPromise();
            }
    }
};
exports.lookupValue = lookupValue;
const lookupProximity = async (onlineLookup, offlineLookup, strategy, lookupParams) => {
    // These functions are just helpers to return an appropriate promisified version of the lookup
    const onlineLookupPromise = buildProximityPromise(onlineLookup, lookupParams);
    const offlineLookupPromise = buildProximityPromise(offlineLookup, lookupParams);
    strategy = getStrategyForAccessors(strategy, onlineLookup, offlineLookup);
    // use the strategy to decide how to perform the lookup
    switch (strategy) {
        case 'Online Only':
            return onlineLookupPromise();
        case 'Offline Only':
            return offlineLookupPromise();
        case 'Both':
            return Promise.all([onlineLookupPromise(), offlineLookupPromise()])
                .then(promiseResults => {
                // When checking both, we should combine the results and use the lookupSucceeded value from the online lookup
                const results = [];
                if (promiseResults[0].lookupSucceeded) {
                    results.push(...promiseResults[0].results);
                }
                if (promiseResults[1].lookupSucceeded) {
                    for (const foundOfflineItem of promiseResults[1].results) {
                        const foundOnlineItemIndex = results.findIndex(existingResult => existingResult.record.id === foundOfflineItem.record.id);
                        // If we find the item both online and off, use the offline one
                        //TODO: Change this to use the lastUpdatedTime to resolve conflicts
                        if (foundOnlineItemIndex < 0) {
                            //We don't have it already, so add it
                            results.push(foundOfflineItem);
                        }
                        else {
                            //replace the online version with our offline one, in case it hasn't been synced yet.
                            results[foundOnlineItemIndex] = foundOfflineItem;
                        }
                    }
                }
                return {
                    results: results.slice(0, lookupParams.limit),
                    lookupSucceeded: promiseResults[0].lookupSucceeded && promiseResults[1].lookupSucceeded,
                };
            })
                .catch(() => ({ results: [], lookupSucceeded: false }));
        case 'Offline with Online Fallback':
            const offlineResults = await offlineLookupPromise();
            if (offlineResults && offlineResults.results.length > 0) {
                return offlineResults;
            }
            else {
                return onlineLookupPromise();
            }
        case 'Online with Offline Fallback':
            const onlineResults = await onlineLookupPromise();
            if (onlineResults && onlineResults.results.length > 0) {
                return onlineResults;
            }
            else {
                return offlineLookupPromise();
            }
    }
};
exports.lookupProximity = lookupProximity;
const lookupUser = async (onlineLookup, offlineLookup, strategy, lookupParams) => {
    // These functions are just helpers to return an appropriate promisified version of the lookup
    const onlineLookupPromise = buildUserPromise(onlineLookup, lookupParams);
    const offlineLookupPromise = buildUserPromise(offlineLookup, lookupParams);
    strategy = getStrategyForAccessors(strategy, onlineLookup, offlineLookup);
    // use the strategy to decide how to perform the lookup
    switch (strategy) {
        case 'Online Only':
            return onlineLookupPromise();
        case 'Offline Only':
            return offlineLookupPromise();
        case 'Both':
            return Promise.all([onlineLookupPromise(), offlineLookupPromise()])
                .then(promiseResults => {
                var _a;
                // When checking both, we should fallback the result and use the lookupSucceeded value from both
                // If we find the item both online and off, use the offline one, in case it hasn't been synced yet.
                //TODO: Change this to use the lastUpdatedTime to resolve conflicts?
                return {
                    result: (_a = promiseResults[1].result) !== null && _a !== void 0 ? _a : promiseResults[0].result,
                    lookupSucceeded: promiseResults[0].lookupSucceeded && promiseResults[1].lookupSucceeded,
                };
            })
                .catch(() => ({ result: null, lookupSucceeded: false }));
        case 'Offline with Online Fallback':
            const offlineResult = await offlineLookupPromise();
            if (offlineResult && offlineResult.result) {
                return offlineResult;
            }
            else {
                return onlineLookupPromise();
            }
        case 'Online with Offline Fallback':
            const onlineResult = await onlineLookupPromise();
            if (onlineResult && onlineResult.result) {
                return onlineResult;
            }
            else {
                return offlineLookupPromise();
            }
    }
};
exports.lookupUser = lookupUser;
const hasStrategyPreReqs = (strategy, onlineLookup, offlineLookup) => {
    strategy = getStrategyForAccessors(strategy, onlineLookup, offlineLookup);
    switch (strategy) {
        case 'Both':
            return !!offlineLookup && !!onlineLookup;
        case 'Offline with Online Fallback':
        case 'Online with Offline Fallback':
            return !!offlineLookup || !!onlineLookup;
        case 'Offline Only':
            return !!offlineLookup;
        case 'Online Only':
            return !!onlineLookup;
    }
};
exports.hasStrategyPreReqs = hasStrategyPreReqs;
//# sourceMappingURL=LookupHelper.js.map